import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj) return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  if (typeof obj !== 'object') return obj;

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const reviewService = {
  // Get reviews for a photographer
  async getPhotographerReviews(photographerId) {
    // First, get reviews
    const { data: reviewsData, error: reviewsError } = await supabase
      ?.from('reviews')
      ?.select('*')
      ?.eq('photographer_id', photographerId)
      ?.order('created_at', { ascending: false });

    if (reviewsError) throw reviewsError;
    if (!reviewsData || reviewsData.length === 0) return [];

    // Get customer profiles
    const customerIds = [...new Set(reviewsData.map(r => r.customer_id).filter(Boolean))];
    let customerProfiles = {};
    
    if (customerIds.length > 0) {
      const { data: profiles, error: profilesError } = await supabase
        ?.from('user_profiles')
        ?.select('id, full_name, avatar_url')
        ?.in('id', customerIds);

      if (!profilesError && profiles) {
        profiles.forEach(profile => {
          customerProfiles[profile.id] = profile;
        });
      }
    }

    // Get booking info (photography_type) for each review
    const bookingIds = [...new Set(reviewsData.map(r => r.booking_id).filter(Boolean))];
    let bookingsMap = {};

    if (bookingIds.length > 0) {
      const { data: bookings, error: bookingsError } = await supabase
        ?.from('bookings')
        ?.select('id, photography_type')
        ?.in('id', bookingIds);

      if (!bookingsError && bookings) {
        bookings.forEach(b => {
          bookingsMap[b.id] = b;
        });
      }
    }

    // Combine reviews with customer profiles and booking info
    return reviewsData.map(review => toCamelCase({
      ...review,
      customer: customerProfiles[review.customer_id] || null,
      photography_type: bookingsMap[review.booking_id]?.photography_type || null
    }));
  },

  // Create review
  async createReview(reviewData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const reviewPayload = toSnakeCase({
      ...reviewData,
      customerId: user?.id
    });

    const { data, error } = await supabase?.from('reviews')?.insert(reviewPayload)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  },

  // Update review
  async updateReview(reviewId, reviewData) {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) throw new Error('Not authenticated');

    const reviewPayload = toSnakeCase(reviewData);

    const { data, error } = await supabase?.from('reviews')?.update(reviewPayload)?.eq('id', reviewId)?.eq('customer_id', user?.id)?.select()?.single();

    if (error) throw error;
    return toCamelCase(data);
  }
};